import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { Row, Col, Container } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { Button } from '@abroad/components'
import ScheduledTab from './ScheduledTab'
import ToBeScheduledTab from './ToBeScheduledTab'
import CompletedTab from './CompletedTab'

export const BOOKING_SUB_TAB_TYPES = {
  SCHEDULED: 'scheduled',
  TO_BE_SCHEDULED: 'to-be-scheduled',
  COMPLETED: 'completed',
}

const BookingTab = ({ timezone, isCalendarConnected }) => {
  const location = useLocation()
  const [activeTab, setActiveTab] = useState(BOOKING_SUB_TAB_TYPES.SCHEDULED)
  const [filterRender, setFilterRender] = useState(null)
  const [highlightDate, setHighlightDate] = useState(null)
  const [highlightEventIds, setHighlightEventIds] = useState([])
  const rootRef = useRef(null)
  const subTabsRef = useRef(null)
  const [subTabsWidth, setSubTabsWidth] = useState(null)

  // List titles use this width so the "See my availability" button ends
  // exactly below the right edge of the last sub-tab.
  useLayoutEffect(() => {
    const lastSubTab = subTabsRef.current?.lastElementChild
    if (!lastSubTab || !rootRef.current) return

    const updateWidth = () => {
      setSubTabsWidth(
        lastSubTab.getBoundingClientRect().right -
          rootRef.current.getBoundingClientRect().left,
      )
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateWidth)
        : null
    if (resizeObserver) {
      resizeObserver.observe(lastSubTab)
    }

    return () => {
      window.removeEventListener('resize', updateWidth)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [])

  // Read highlight data from location state
  useEffect(() => {
    if (location.state) {
      if (location.state.highlightDate) {
        setHighlightDate(location.state.highlightDate)
        setHighlightEventIds(location.state.highlightEventIds || [])
        // Switch to scheduled tab if not already there
        if (activeTab !== BOOKING_SUB_TAB_TYPES.SCHEDULED) {
          setActiveTab(BOOKING_SUB_TAB_TYPES.SCHEDULED)
        }
        // Clear location state after reading
        window.history.replaceState({}, document.title)
      }
    }
  }, [location.state, activeTab])

  const handleTabChange = (tab) => {
    // Only reset filter when switching to a different tab
    if (activeTab !== tab) {
      setFilterRender(null) // Reset filter when switching tabs
      // Clear highlight when switching tabs
      setHighlightDate(null)
      setHighlightEventIds([])
    }
    setActiveTab(tab)
  }

  return (
    <div
      ref={rootRef}
      style={
        subTabsWidth
          ? { '--booking-subtabs-width': `${subTabsWidth}px` }
          : undefined
      }>
      <Container fluid className='px-lg-0 mb-4'>
        <Row className='align-items-center'>
          <Col xs={12} md={6} lg={6} className='mb-3 mb-md-0'>
            <div className='d-flex' ref={subTabsRef}>
              <Button
                variant='none'
                className='navTabs ml-0'
                style={{
                  background:
                    activeTab === BOOKING_SUB_TAB_TYPES.SCHEDULED
                      ? '#0A223C'
                      : '',
                  color:
                    activeTab === BOOKING_SUB_TAB_TYPES.SCHEDULED ? '#fff' : '',
                }}
                onClick={() =>
                  handleTabChange(BOOKING_SUB_TAB_TYPES.SCHEDULED)
                }>
                SCHEDULED
              </Button>
              <Button
                variant='none'
                className='navTabs'
                style={{
                  background:
                    activeTab === BOOKING_SUB_TAB_TYPES.TO_BE_SCHEDULED
                      ? '#0A223C'
                      : '',
                  color:
                    activeTab === BOOKING_SUB_TAB_TYPES.TO_BE_SCHEDULED
                      ? '#fff'
                      : '',
                }}
                onClick={() =>
                  handleTabChange(BOOKING_SUB_TAB_TYPES.TO_BE_SCHEDULED)
                }>
                TO BE SCHEDULED
              </Button>
              <Button
                variant='none'
                className='navTabs'
                style={{
                  background:
                    activeTab === BOOKING_SUB_TAB_TYPES.COMPLETED
                      ? '#0A223C'
                      : '',
                  color:
                    activeTab === BOOKING_SUB_TAB_TYPES.COMPLETED ? '#fff' : '',
                }}
                onClick={() =>
                  handleTabChange(BOOKING_SUB_TAB_TYPES.COMPLETED)
                }>
                COMPLETED
              </Button>
            </div>
          </Col>
          <Col
            xs={12}
            md={6}
            lg={6}
            className='d-flex justify-content-end align-items-center'>
            {filterRender?.inputField || filterRender}
          </Col>
        </Row>
      </Container>
      {activeTab === BOOKING_SUB_TAB_TYPES.SCHEDULED && (
        <ScheduledTab
          timezone={timezone}
          isCalendarConnected={isCalendarConnected}
          onFilterRender={setFilterRender}
          highlightDate={highlightDate}
          highlightEventIds={highlightEventIds}
          onHighlightClear={() => {
            setHighlightDate(null)
            setHighlightEventIds([])
          }}
        />
      )}
      {activeTab === BOOKING_SUB_TAB_TYPES.TO_BE_SCHEDULED && (
        <ToBeScheduledTab
          timezone={timezone}
          isCalendarConnected={isCalendarConnected}
          onFilterRender={setFilterRender}
          onScheduleSuccess={() =>
            handleTabChange(BOOKING_SUB_TAB_TYPES.SCHEDULED)
          }
        />
      )}
      {activeTab === BOOKING_SUB_TAB_TYPES.COMPLETED && (
        <CompletedTab
          timezone={timezone}
          isCalendarConnected={isCalendarConnected}
          onFilterRender={setFilterRender}
        />
      )}
    </div>
  )
}

export default BookingTab
